"use client"

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import { FragmentType, graphql, useFragment } from "@/gql"
import { VideoElement } from "@/elements"
import { PlayIcon } from "@heroicons/react/24/outline"
import clsx from "clsx"

const VideoFragment = graphql(`
  fragment Video on CreativeWorkPortfolioProjectFile {
    id
    bestAvailableVideoFile
    metadata {
      width
      height
      rotation
    }
  }
`)

interface Props extends React.VideoHTMLAttributes<HTMLVideoElement> {
  video: FragmentType<typeof VideoFragment>
  poster: string
  controls?: boolean
  videoClassName?: string
}

export interface VideoHandle {
  play: () => Promise<void>
  pause: () => void
}

export const Video = forwardRef<VideoHandle, Props>(
  ({ video: videoData, poster, controls = true, videoClassName, ...rest }, ref) => {
    const video = useFragment(VideoFragment, videoData)
    const [hasPlayed, setHasPlayed] = useState(false)
    const [posterLoaded, setPosterLoaded] = useState(false)
    const [isLandScape, setIsLandScape] = useState<boolean | null>(null)
    const videoRef = useRef<VideoHandle>(null)

    const hasAutoPlay = rest.autoPlay

    useImperativeHandle(ref, () => ({
      play: () => videoRef.current?.play() ?? Promise.resolve(),
      pause: () => videoRef.current?.pause(),
    }))

    const handlePlay = () => {
      if (hasPlayed || !videoRef.current) return
      videoRef.current?.play()
      setHasPlayed(true)
    }

    const handlePosterLoad = (img: HTMLImageElement) => {
      const imageWidth = img.width
      const imageHeight = img.height
      if (imageWidth && imageHeight) {
        setIsLandScape(imageWidth > imageHeight)
      }

      setPosterLoaded(true)
    }

    useEffect(() => {
      if (!poster) return

      // Check if the poster has loaded
      const img = new Image()
      img.src = poster

      if (img.complete) {
        handlePosterLoad(img)
      } else {
        img.onload = () => {
          handlePosterLoad(img)
        }
        img.onerror = () => {
          console.error("Failed to load poster image.")
        }
      }
    }, [poster, handlePosterLoad])

    return (
      <div className={clsx("relative flex h-full w-full items-center justify-center")} onClick={handlePlay}>
        <VideoElement
          ref={videoRef}
          src={video?.bestAvailableVideoFile || ""}
          className={clsx(
            isLandScape === true
              ? "my-auto w-full"
              : isLandScape === false
                ? "mx-auto h-full w-auto"
                : "h-full w-full object-contain",
            videoClassName
          )}
          poster={poster}
          controls={hasPlayed && controls}
          {...rest}
        />
        {!hasPlayed && posterLoaded && !hasAutoPlay && (
          <div className="absolute left-1/2 top-1/2 flex h-[50px] w-[50px] -translate-x-1/2 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full bg-bg-1">
            <PlayIcon className="size-[22px] fill-fg-1" />
          </div>
        )}
      </div>
    )
  }
)

Video.displayName = "Video"
